import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, Spinner, Card, Row, Col, InputGroup } from 'react-bootstrap';
import { FaCar, FaUsers, FaSuitcase, FaSave, FaArrowLeft, FaDollarSign } from 'react-icons/fa';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

// All price fields from database
const PRICE_FIELDS = [
  { name: 'pricefive_km', label: '5 km Price' },
  { name: 'priceten_km', label: '10 km Price' },
  { name: 'pricefifteen_km', label: '15 km Price' },
  { name: 'pricetwenty_km', label: '20 km Price' },
  { name: 'pricetwentyfive_km', label: '25 km Price' },
  { name: 'pricethirty_km', label: '30 km Price' },
  { name: 'pricethirtyfive_km', label: '35 km Price' },
  { name: 'priceforty_km', label: '40 km Price' },
  { name: 'pricefortyfive_km', label: '45 km Price' },
  { name: 'pricefifty_km', label: '50 km Price' },
  { name: 'pricefiftyfive_km', label: '55 km Price' },
  { name: 'pricesixty_km', label: '60 km Price' },
  { name: 'pricesixtyfive_km', label: '65 km Price' },
  { name: 'priceseventy_km', label: '70 km Price' },
  { name: 'priceseventyfive_km', label: '75 km Price' },
  { name: 'priceeighty_km', label: '80 km Price' },
  { name: 'priceeightyfive_km', label: '85 km Price' },
  { name: 'priceninety_km', label: '90 km Price' },
  { name: 'priceninetyfive_km', label: '95 km Price' },
  { name: 'pricehundered_km', label: '100 km Price' }
];

const initialVehicleState = {
  Vehicle_ID: '',
  Vehicle_Name: '',
  imageUrl: '',
  description: '',
  Vehicle_Category: 'Luxury',
  Seating_Capacity: 4,
  Luggage: 2,
  Basic_Distance: 5,
  Basic_Price: 0,
  Price_Per_Km: 0,
  // Initialize all price fields
  ...PRICE_FIELDS.reduce((acc, field) => ({ ...acc, [field.name]: 0 }), {})
};

const AddEditVehicle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  
  const [vehicle, setVehicle] = useState(initialVehicleState);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isEdit) {
      const fetchVehicle = async () => {
        try {
          setLoading(true);
          const token = localStorage.getItem('adminToken');
          const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/vehicles/${id}`, {
            headers: { 'x-auth-token': token }
          });
          
          setVehicle({
            ...initialVehicleState,
            ...response.data
          });
        } catch (err) {
          console.error('Error fetching vehicle:', err);
          setError(err.response?.data?.error || 'Failed to load vehicle');
        } finally {
          setLoading(false);
        }
      };
      fetchVehicle();
    } else {
      const generateVehicleId = async () => {
        try {
          const token = localStorage.getItem('adminToken');
          const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/vehicles/last-id`, {
            headers: { 'x-auth-token': token }
          });
          const lastId = response.data.lastId || 0;
          setVehicle(prev => ({
            ...prev,
            Vehicle_ID: lastId + 1
          }));
        } catch (err) {
          console.error('Error generating vehicle ID:', err);
        }
      };
      generateVehicleId();
    }
  }, [id, isEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setVehicle(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePriceChange = (e) => {
    const { name, value } = e.target;
    setVehicle(prev => ({
      ...prev,
      [name]: value === '' ? '' : parseFloat(value) || 0
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('adminToken');
      
      if (isEdit) {
        await axios.put(`${process.env.REACT_APP_API_URL}/admin/vehicles/${id}`,
          vehicle,
          { headers: { 'x-auth-token': token } }
        );
        toast.success('Vehicle updated successfully');
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/admin/vehicles`,
          vehicle,
          { headers: { 'x-auth-token': token } }
        );
        toast.success('Vehicle added successfully');
      }
      
      navigate('/admin/vehicles');
    } catch (err) {
      console.error('Error saving vehicle:', err);
      setError(err.response?.data?.error || 'Failed to save vehicle');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return <div className="text-center my-5"><Spinner animation="border" variant="primary" /></div>;
  }

  return (
    <div className="vehicle-form-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>{isEdit ? 'Edit Vehicle' : 'Add New Vehicle'}</h2>
        <Button variant="secondary" onClick={() => navigate('/admin/vehicles')}>
          <FaArrowLeft className="me-2" /> Back to Vehicles
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Vehicle ID</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>#</InputGroup.Text>
                    <Form.Control
                      type="text"
                      name="Vehicle_ID"
                      value={vehicle.Vehicle_ID}
                      onChange={handleChange}
                      required
                      disabled={isEdit}
                    />
                  </InputGroup>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Vehicle Name</Form.Label>
                  <InputGroup>
                    <InputGroup.Text><FaCar /></InputGroup.Text>
                    <Form.Control
                      type="text"
                      name="Vehicle_Name"
                      value={vehicle.Vehicle_Name}
                      onChange={handleChange}
                      required
                    />
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>





            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Vehicle Image URL</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>#</InputGroup.Text>
                    <Form.Control
                      type="text"
                      name="imageUrl"
                      value={vehicle.imageUrl}
                      onChange={handleChange}
                      required
                    />
                  </InputGroup>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Vehicle Description</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>#</InputGroup.Text>
                    <Form.Control
                      type="text"
                      name="description"
                      value={vehicle.description}
                      onChange={handleChange}
                      required
                    />
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>









            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Category</Form.Label>
                  <Form.Select
                    name="Vehicle_Category"
                    value={vehicle.Vehicle_Category}
                    onChange={handleChange}
                    required
                  >
                    <option value="Luxury">Luxury</option>
                    <option value="Premium">Premium</option>
                    <option value="Extra Premium">Extra Premium</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Seating Capacity</Form.Label>
                  <Form.Control
                    type="number"
                    name="Seating_Capacity"
                    value={vehicle.Seating_Capacity}
                    onChange={handleChange}
                    min="1"
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Luggage Capacity</Form.Label>
                  <Form.Control
                    type="number"
                    name="Luggage"
                    value={vehicle.Luggage}
                    onChange={handleChange}
                    min="0"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Basic Distance (km)</Form.Label>
                  <Form.Control
                    type="number"
                    name="Basic_Distance"
                    value={vehicle.Basic_Distance}
                    onChange={handleChange}
                    min="1"
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Basic Price</Form.Label>
                  <InputGroup>
                    <InputGroup.Text><FaDollarSign /></InputGroup.Text>
                    <Form.Control
                      type="number"
                      name="Basic_Price"
                      value={vehicle.Basic_Price}
                      onChange={handleChange}
                      min="0"
                      step="0.01"
                      required
                    />
                  </InputGroup>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Price Per Km</Form.Label>
                  <InputGroup>
                    <InputGroup.Text><FaDollarSign /></InputGroup.Text>
                    <Form.Control
                      type="number"
                      name="Price_Per_Km"
                      value={vehicle.Price_Per_Km}
                      onChange={handleChange}
                      min="0"
                      step="0.01"
                      required
                    />
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>

            <h5 className="mt-4 mb-3">Distance-Based Pricing</h5>
            <Row>
              {PRICE_FIELDS.map((field) => (
                <Col key={field.name} md={4} className="mb-3">
                  <Form.Group>
                    <Form.Label>{field.label}</Form.Label>
                    <InputGroup>
                      <InputGroup.Text><FaDollarSign /></InputGroup.Text>
                      <Form.Control
                        type="number"
                        name={field.name}
                        value={vehicle[field.name] ?? ''}
                        onChange={handlePriceChange}
                        min="0"
                        step="0.01"
                        required
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>
              ))}
            </Row>

            <div className="d-flex justify-content-end mt-4">
              <Button variant="primary" type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <><Spinner animation="border" size="sm" className="me-2" /> Saving...</>
                ) : (
                  <><FaSave className="me-2" />{isEdit ? 'Update Vehicle' : 'Add Vehicle'}</>
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default AddEditVehicle;